import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast"; 
import { Loader2, FileText, AlertTriangle } from "lucide-react";
import { generateSurveyPDF, checkFpiPdfExists } from "@/utils/surveyPdfGenerator";
import type { SupportedLanguage } from "@/components/survey/LanguageSelector";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { Group } from "@/types/groups";

const LANGUAGES = [
  { code: "sv", label: "Svenska" },
  { code: "en", label: "English / Engelska" },
  { code: "ar", label: "العربية / Arabiska" },
  { code: "fa", label: "فارسی / Persiska" },
  { code: "prs", label: "دری / Dari" },
  { code: "so", label: "Soomaali / Somaliska" },
  { code: "ti", label: "ትግርኛ / Tigrinja" },
  { code: "uk", label: "Українська / Ukrainska" }
];

const getSurveyOptions = (assignedGroup: "intervention" | "control" | null) => {
  if (assignedGroup === "intervention") {
    return [
      { value: "1", label: "Första enkäten (Första FÖS-träffen)" },
      { value: "2", label: "Andra enkäten (Sista FÖS-träffen)" },
      { value: "3", label: "Tredje enkäten (6 månader efter start)" }
    ];
  } else {
    return [
      { value: "1", label: "Första enkäten (Tematräffen)" },
      { value: "2", label: "Andra enkäten (Sista FÖS-träffen)" },
      { value: "3", label: "Tredje enkäten (6 månader efter FÖS-start)" }
    ];
  }
};

interface PaperBackupGeneratorProps {
  groupId?: string;
  group?: Group;
}

export const PaperBackupGenerator = ({ groupId, group }: PaperBackupGeneratorProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>("sv");
  const [selectedSurvey, setSelectedSurvey] = useState("1");
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const { toast } = useToast();

  // Check if FPI PDF exists when language changes
  const handleLanguageChange = async (language: SupportedLanguage) => {
    setSelectedLanguage(language);
    setPdfError(null);
    try {
      const exists = await checkFpiPdfExists(language);
      if (!exists) {
        setPdfError(`FPI dokument saknas för ${LANGUAGES.find(lang => lang.code === language)?.label}`);
      }
    } catch (error) {
      console.error("Error checking FPI PDF:", error);
    }
  };

  const handleGeneratePDF = async () => {
    setIsGenerating(true);
    setPdfError(null);
    try {
      toast({
        title: "Genererar PDF",
        description: "Skapar PDF med enkätfrågor och FPI information...",
      });
      
      // Check if FPI PDF exists before attempting to generate
      const exists = await checkFpiPdfExists(selectedLanguage);
      if (!exists) {
        throw new Error(`FPI dokument saknas för ${selectedLanguage}`);
      }
      
      // Generate the combined PDF with FPI information and survey questions
      await generateSurveyPDF(selectedLanguage, selectedSurvey);
      
      const surveyOptions = getSurveyOptions(group?.assigned_group || null);
      const surveyLabel = surveyOptions.find(opt => opt.value === selectedSurvey)?.label || "";
      
      toast({
        title: "PDF genererad",
        description: `Komplett PDF för ${surveyLabel} har skapats med FPI information på ${LANGUAGES.find(lang => lang.code === selectedLanguage)?.label}.`,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      setPdfError(`Kunde inte generera PDF: ${error instanceof Error ? error.message : 'Okänt fel'}`);
      toast({
        title: "Fel vid generering",
        description: `Kunde inte generera PDF. ${error instanceof Error ? error.message : 'Kontrollera att det finns FPI dokument för det valda språket.'}`,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Show group info if available
  const groupInfo = group && (
    <div className="mb-4 p-3 bg-muted rounded-lg text-sm">
      <p><strong>Grupp:</strong> {group.name || group.id.slice(0, 8)}</p>
      <p><strong>Tilldelning:</strong> {group.assigned_group === "intervention" ? "Intervention" : "Kontroll"}</p>
    </div>
  );

  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">Backup på papper</h2>
      
      {groupInfo}
      
      <div className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="language-select">Välj språk</Label>
          <Select
            value={selectedLanguage}
            onValueChange={(value) => handleLanguageChange(value as SupportedLanguage)}
          >
            <SelectTrigger id="language-select">
              <SelectValue placeholder="Välj språk" />
            </SelectTrigger>
            <SelectContent>
              {LANGUAGES.map((lang) => (
                <SelectItem key={lang.code} value={lang.code}>
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {pdfError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{pdfError}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label>Välj träff</Label>
          <RadioGroup
            value={selectedSurvey}
            onValueChange={setSelectedSurvey}
            className="flex flex-col space-y-2"
          >
            {getSurveyOptions(group?.assigned_group || null).map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <RadioGroupItem value={option.value} id={`survey-${option.value}`} />
                <Label htmlFor={`survey-${option.value}`}>{option.label}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <Button 
          onClick={handleGeneratePDF} 
          disabled={isGenerating || !!pdfError}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Genererar...
            </>
          ) : (
            <>
              <FileText className="mr-2 h-4 w-4" />
              Generera PDF
            </>
          )}
        </Button>
      </div>
    </Card>
  );
};
